import { withAuthRecovery } from '@/lib/authRecovery'

type StudioStatusHistoryRow = {
  history_id?: number | null
  studio_internal_id: number
  old_status_code: string | null
  new_status_code: string | null
  changed_at: string | null
}

type SupabaseLike = {
  from: (table: string) => {
    select: (columns: string) => any
  }
}

function compareStatusHistoryRows(left: StudioStatusHistoryRow, right: StudioStatusHistoryRow): number {
  const leftDate = String(left.changed_at ?? '')
  const rightDate = String(right.changed_at ?? '')
  if (leftDate !== rightDate) return leftDate.localeCompare(rightDate)
  return Number(left.history_id ?? 0) - Number(right.history_id ?? 0)
}

export function resolveEffectiveStudioStatus(
  studioId: number,
  metricDate: string,
  currentStatusByStudioId: Map<number, string>,
  statusHistoryByStudioId: Map<number, StudioStatusHistoryRow[]>
): string {
  const currentStatus = currentStatusByStudioId.get(studioId) ?? 'CLOSED'
  const historyRows = statusHistoryByStudioId.get(studioId) ?? []

  const latestPastChange = [...historyRows]
    .sort((left, right) => compareStatusHistoryRows(right, left))
    .find((row) => String(row.changed_at).slice(0, 10) <= metricDate)
  if (latestPastChange?.new_status_code) return latestPastChange.new_status_code

  const nearestFutureChange = [...historyRows]
    .sort(compareStatusHistoryRows)
    .find((row) => String(row.changed_at).slice(0, 10) > metricDate)
  if (nearestFutureChange?.old_status_code) return nearestFutureChange.old_status_code

  return currentStatus
}

export async function loadEffectiveStudioStatusMap(
  supabase: SupabaseLike,
  studioIds: number[],
  metricDate: string,
  currentStatusByStudioId?: Map<number, string>
): Promise<Map<number, string>> {
  const uniqueStudioIds = Array.from(new Set(studioIds.filter((studioId) => Number.isFinite(studioId) && studioId > 0)))
  if (uniqueStudioIds.length === 0) return new Map<number, string>()

  const currentStatuses = new Map(currentStatusByStudioId || [])
  const missingStudioIds = uniqueStudioIds.filter((studioId) => !currentStatuses.has(studioId))

  const [missingStatusesResult, historyResult] = await Promise.all([
    missingStudioIds.length === 0
      ? Promise.resolve({ data: [] as Array<{ studio_internal_id: number; status_code: string | null }>, error: null })
      : withAuthRecovery(() =>
          supabase
            .from('studios')
            .select('studio_internal_id, status_code')
            .in('studio_internal_id', missingStudioIds)
        ) as Promise<{ data: Array<{ studio_internal_id: number; status_code: string | null }> | null; error: { message?: string } | null }>,
    withAuthRecovery(() =>
      supabase
        .from('studio_status_history')
        .select('history_id, studio_internal_id, old_status_code, new_status_code, changed_at')
        .in('studio_internal_id', uniqueStudioIds)
    ) as Promise<{ data: StudioStatusHistoryRow[] | null; error: { message?: string } | null }>
  ])

  if (missingStatusesResult.error) {
    throw new Error(missingStatusesResult.error.message || 'Не удалось загрузить текущие статусы студий')
  }
  if (historyResult.error) {
    throw new Error(historyResult.error.message || 'Не удалось загрузить историю статусов студий')
  }

  for (const row of missingStatusesResult.data || []) {
    currentStatuses.set(row.studio_internal_id, row.status_code ?? 'CLOSED')
  }

  const statusHistoryByStudioId = new Map<number, StudioStatusHistoryRow[]>()
  for (const row of historyResult.data || []) {
    if (!row.changed_at) continue
    const current = statusHistoryByStudioId.get(row.studio_internal_id) ?? []
    current.push(row)
    statusHistoryByStudioId.set(row.studio_internal_id, current)
  }

  const effectiveStatusByStudioId = new Map<number, string>()
  for (const studioId of uniqueStudioIds) {
    effectiveStatusByStudioId.set(
      studioId,
      resolveEffectiveStudioStatus(studioId, metricDate, currentStatuses, statusHistoryByStudioId)
    )
  }

  return effectiveStatusByStudioId
}

export function isRatingEligibleStudioStatus(statusCode: string | null | undefined): boolean {
  return statusCode === 'OPEN'
}
